import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Sparkles, Loader2, ListChecks, FileText } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const getAuthHeaders = () => {
  const token = localStorage.getItem("session_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const STYLES = [
  { id: "brief", label: "Resumen rápido", icon: Sparkles },
  { id: "detailed", label: "Resumen detallado", icon: FileText },
  { id: "tasks", label: "Tareas pendientes", icon: ListChecks },
];

export default function ConversationSummaryDialog({ open, onOpenChange, conversationId, conversationName }) {
  const [style, setStyle] = useState("brief");
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState("");
  const [meta, setMeta] = useState(null);

  const generate = async (selectedStyle = style) => {
    if (!conversationId) return;
    setLoading(true);
    setSummary("");
    try {
      const res = await fetch(`${API}/api/conversations/${conversationId}/summary`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        credentials: "include",
        body: JSON.stringify({ limit: 100, style: selectedStyle }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Error al generar resumen");
      }
      const data = await res.json();
      setSummary(data.summary || "Sin resumen disponible.");
      setMeta({ count: data.message_count, style: data.style });
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) { setSummary(""); setMeta(null); } }}>
      <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-400" />
            Resumen IA {conversationName ? `· ${conversationName}` : ""}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-2">
          {STYLES.map((s) => {
            const Icon = s.icon;
            const active = style === s.id;
            return (
              <button
                key={s.id}
                onClick={() => { setStyle(s.id); generate(s.id); }}
                disabled={loading}
                className={`p-3 rounded-lg border transition text-left ${
                  active
                    ? "bg-amber-600/20 border-amber-500 text-amber-200"
                    : "bg-slate-800 border-slate-700 text-slate-300 hover:border-slate-600"
                }`}
              >
                <Icon className="w-4 h-4 mb-1" />
                <p className="text-sm font-medium">{s.label}</p>
              </button>
            );
          })}
        </div>

        <div className="min-h-[180px] bg-slate-800/60 border border-slate-700 rounded-lg p-4">
          {loading ? (
            <div className="flex items-center justify-center h-full text-slate-400">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Analizando conversación…
            </div>
          ) : summary ? (
            <div className="space-y-2">
              <p className="text-slate-100 whitespace-pre-wrap leading-relaxed">{summary}</p>
              {meta && (
                <p className="text-xs text-slate-500 pt-2 border-t border-slate-700">
                  Basado en {meta.count} mensajes recientes.
                </p>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-slate-400 text-center">
              <Sparkles className="w-8 h-8 text-amber-400/60 mb-2" />
              <p>Elige un estilo de resumen para ponerte al día.</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-slate-300">Cerrar</Button>
          {summary && (
            <Button onClick={() => generate()} disabled={loading} className="bg-amber-600 hover:bg-amber-700">
              Regenerar
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
